/* WeatherDecisionCard — Phase 62.6 (May 8, 2026) MULTI-CONDITION HERO
 *
 * The single most-actionable verdict for the user's current conditions.
 * Adapts to season + sky + air, not just rain. Priority-ranked engine
 * picks ONE verdict from:
 *   1. Active rain
 *   2. Extreme heat (≥40 °C or apparent ≥45 °C)
 *   3. Extreme cold (≤5 °C or apparent ≤0 °C)
 *   4. Fog / low visibility (<1 km)
 *   5. Poor air quality (US AQI ≥150)
 *   6. Strong wind (>40 km/h gusts)
 *   7. Strong UV (≥8) on a clear day
 *   8. Rain coming in next 6 h
 *   9. Pleasant default
 *
 * Each branch uses its own colour palette + icon + plain-English action.
 * One card, one job: tell the user what to do RIGHT NOW for their
 * climate, not just whether to grab an umbrella.
 */
import { motion } from "framer-motion";
import { useMemo } from "react";
import {
  Umbrella,
  Sun,
  CloudRain,
  CloudSun,
  CloudFog,
  Drop,
  ClockClockwise,
  Thermometer,
  Snowflake,
  Wind,
  MaskSad,
  Leaf,
} from "@phosphor-icons/react";

const NUM = {
  fontFamily: "Manrope, system-ui, sans-serif",
  fontWeight: 800,
  letterSpacing: "-0.04em",
  fontFeatureSettings: "'tnum' 1",
};

/* ---------------- helpers ---------------- */

function fmtTimeFromIso(iso) {
  if (!iso) return null;
  const m = /T(\d{2}):(\d{2})/.exec(iso);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const mm = m[2];
  const suffix = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${mm} ${suffix}`;
}

/** Find next dry/wet hour transitions in the forecast horizon. */
function rainTransitions(hourlyT, hourlyP, nowIdx, lookAhead = 12) {
  let firstWetIdx = -1;
  let firstDryAfterWet = -1;
  let peakMmh = 0;
  for (let i = 0; i < Math.min(lookAhead, hourlyP.length - nowIdx); i++) {
    const v = hourlyP[nowIdx + i] || 0;
    peakMmh = Math.max(peakMmh, v);
    if (v >= 0.3) {
      if (firstWetIdx === -1) firstWetIdx = i;
      firstDryAfterWet = -1;
    } else if (firstWetIdx !== -1 && firstDryAfterWet === -1) {
      firstDryAfterWet = i;
    }
  }
  return {
    peakMmh,
    startTime: firstWetIdx !== -1 ? fmtTimeFromIso(hourlyT[nowIdx + firstWetIdx]) : null,
    stopTime: firstDryAfterWet !== -1 ? fmtTimeFromIso(hourlyT[nowIdx + firstDryAfterWet]) : null,
    firstWetIdx,
  };
}

/** Today's accumulated + expected rainfall (mm). */
function rainfallTotals(hourlyT, hourlyP, daily, nowIdx, dayKey) {
  let runningPast = 0;
  let runningFull = 0;
  if (dayKey) {
    for (let i = 0; i < hourlyT.length; i++) {
      if (hourlyT[i].slice(0, 10) !== dayKey) continue;
      runningFull += hourlyP[i] || 0;
      if (i < nowIdx) runningPast += hourlyP[i] || 0;
    }
  }
  const todayMm = +runningPast.toFixed(1);
  const expectedMm = +(((daily?.precipitation_sum?.[0] ?? 0) > 0)
    ? daily.precipitation_sum[0]
    : runningFull).toFixed(1);
  return { todayMm, expectedMm };
}

/** Time of day's temperature peak (HH AM/PM). */
function findPeakTempTime(hourlyT, hourlyTemp, nowIdx, dayKey) {
  let bestIdx = nowIdx;
  let bestT = hourlyTemp?.[nowIdx] ?? -Infinity;
  for (let i = nowIdx; i < hourlyT.length; i++) {
    if (hourlyT[i].slice(0, 10) !== dayKey) break;
    if ((hourlyTemp[i] ?? -Infinity) > bestT) {
      bestT = hourlyTemp[i];
      bestIdx = i;
    }
  }
  return { time: fmtTimeFromIso(hourlyT[bestIdx]), temp: bestT };
}

/** Pretty compass bearing (just for body copy). */
function compassFromDeg(deg) {
  if (deg == null) return null;
  const d = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return d[Math.round(deg / 45) % 8];
}

/* ---------------- decision engine ---------------- */

function buildDecision({ forecast, nowcast, aqi }) {
  const cur = forecast?.current || {};
  const hourly = forecast?.hourly || {};
  const daily = forecast?.daily || {};
  const hourlyP = hourly.precipitation || [];
  const hourlyTemp = hourly.temperature_2m || [];
  const hourlyT = hourly.time || [];
  const isoNow = cur.time;

  const nowIdx = isoNow ? Math.max(0, hourlyT.indexOf(`${isoNow.slice(0, 13)}:00`)) : 0;
  const dayKey = isoNow ? isoNow.slice(0, 10) : null;

  const t = cur.temperature_2m;
  const feels = cur.apparent_temperature ?? t;
  const visKm = cur.visibility != null ? cur.visibility / 1000 : null;
  const gusts = cur.wind_gusts_10m ?? cur.wind_speed_10m;
  const aqiVal = aqi?.us_aqi;
  const uvNow = (hourly.uv_index || []).slice(nowIdx, nowIdx + 6).filter((v) => v != null);
  const uvPeak = uvNow.length ? Math.max(...uvNow) : null;
  const code = cur.weather_code;
  const isDay = cur.is_day === 1 || cur.is_day === true;

  const { peakMmh, startTime, stopTime, firstWetIdx } = rainTransitions(hourlyT, hourlyP, nowIdx);
  const { todayMm, expectedMm } = rainfallTotals(hourlyT, hourlyP, daily, nowIdx, dayKey);
  const peakTemp = findPeakTempTime(hourlyT, hourlyTemp, nowIdx, dayKey);

  const curRaining =
    (cur.precipitation || 0) >= 0.1 ||
    (cur.rain || 0) >= 0.1 ||
    (code != null && code >= 51 && code <= 82) ||
    (nowcast?.now_intensity ?? 0) >= 8;

  const nowcastNow = nowcast?.now_intensity ?? 0;
  const nowcastPeak = nowcast?.peak_intensity ?? 0;
  const nowcastPeakMin = nowcast?.peak_minute ?? 0;

  // -------- RAIN (highest user-impact when active) --------
  if (curRaining) {
    const heavy = peakMmh >= 5 || nowcastPeak >= 60;
    const easingSoon = nowcast?.timeline?.length
      ? (() => {
          for (const tl of nowcast.timeline) {
            if (tl.minute > 0 && (tl.intensity ?? 0) < 8 && nowcastNow >= 8) return tl.minute;
          }
          return null;
        })()
      : null;
    if (easingSoon != null && easingSoon <= 30) {
      return {
        accent: "#0284c7", heroIcon: CloudRain,
        kicker: heavy ? "Heavy rain · easing" : "Light rain · easing",
        headline: `Easing in ~${easingSoon} min`,
        sub: stopTime ? `Likely dry by ${stopTime}.` : "Should be dry within the hour.",
        action: { Icon: ClockClockwise, label: "Wait it out — hold the umbrella" },
        rainfall: { todayMm, expectedMm, peakMmh },
      };
    }
    return {
      accent: heavy ? "#1d4ed8" : "#0284c7", heroIcon: CloudRain,
      kicker: heavy ? "Heavy rain right now" : "Light rain right now",
      headline: stopTime ? `Stops around ${stopTime}` : "Steady through the next few hours",
      sub: heavy ? `Up to ${peakMmh.toFixed(1)} mm/h — roads will pool.` :
                   stopTime ? "Should ease through the next few hours." :
                   "No clear break in the next 6 hours.",
      action: {
        Icon: Umbrella,
        label: stopTime
          ? `Take an umbrella till about ${stopTime}`
          : "Carry an umbrella for the rest of the day",
      },
      rainfall: { todayMm, expectedMm, peakMmh },
    };
  }

  // -------- SEVERE / EXTREME HEAT --------
  // India crosses 38 °C routinely in summer; >40 °C is the IMD heatwave
  // threshold for plains. Tier the verdict so users always get a useful
  // call-to-action before they step out into 40 °C dry heat.
  if (t >= 38 || feels >= 42) {
    const xtreme = t >= 42 || feels >= 47;
    return {
      accent: xtreme ? "#b91c1c" : "#ea580c", heroIcon: Thermometer,
      kicker: xtreme ? "Extreme heat alert" : "Hot day · stay cool",
      headline: peakTemp.time
        ? `Peaks at ${peakTemp.temp.toFixed(0)}° around ${peakTemp.time}`
        : `${t.toFixed(0)}° · feels ${feels.toFixed(0)}°`,
      sub: xtreme
        ? `Heatstroke risk — avoid sun 11 AM-4 PM. Drink 3+ litres of water.`
        : `Apparent ${feels.toFixed(0)}° in the sun. Stay hydrated, take breaks indoors.`,
      action: {
        Icon: Thermometer,
        label: xtreme ? "Stay indoors · cooling priority" : "Hydrate often · avoid midday sun",
      },
    };
  }

  // -------- EXTREME COLD --------
  if (t <= 5 || feels <= 0) {
    const xtreme = t <= 0 || feels <= -5;
    return {
      accent: xtreme ? "#1e3a8a" : "#1e40af", heroIcon: Snowflake,
      kicker: xtreme ? "Severe cold" : "Cold · bundle up",
      headline: feels < t ? `${t.toFixed(0)}° · feels ${feels.toFixed(0)}°` : `${t.toFixed(0)}° this morning`,
      sub: xtreme
        ? "Frostbite risk on exposed skin. Layers, gloves, and a hat are essential."
        : "Sweater + jacket weather. Could feel raw in the wind.",
      action: { Icon: Snowflake, label: xtreme ? "Layer up · limit time outdoors" : "Wear a warm jacket" },
    };
  }

  // -------- FOG / LOW VISIBILITY --------
  if (visKm != null && visKm < 1) {
    const dense = visKm < 0.4;
    return {
      accent: "#64748b", heroIcon: CloudFog,
      kicker: dense ? "Dense fog" : "Foggy",
      headline: `Visibility ${(visKm * 1000).toFixed(0)} m`,
      sub: dense
        ? "Drive with hazards on. Air & rail delays likely until visibility lifts."
        : "Drive carefully — keep low beams on. Should clear after sunrise.",
      action: { Icon: CloudFog, label: dense ? "Avoid driving if possible" : "Drive slow · headlights on" },
    };
  }

  // -------- POOR AIR QUALITY --------
  if (aqiVal != null && aqiVal >= 150) {
    const veryUnhealthy = aqiVal >= 200;
    const tone = veryUnhealthy ? "#7c3aed" : "#dc2626";
    return {
      accent: tone, heroIcon: MaskSad,
      kicker: veryUnhealthy ? "Very unhealthy air" : "Unhealthy air",
      headline: `AQI ${Math.round(aqiVal)}${aqi?.label ? ` · ${aqi.label.replace(/-sg|-/g, " ").trim()}` : ""}`,
      sub: veryUnhealthy
        ? "Avoid all outdoor exertion. Run an indoor purifier if possible."
        : "Sensitive groups should limit outdoor time. Mask up if you must go out.",
      action: { Icon: MaskSad, label: veryUnhealthy ? "Stay indoors · purifier on" : "Wear a mask outdoors" },
    };
  }

  // -------- STRONG WIND --------
  if (gusts != null && gusts >= 40) {
    const stormy = gusts >= 60;
    const dir = compassFromDeg(cur.wind_direction_10m);
    return {
      accent: stormy ? "#7c3aed" : "#0891b2", heroIcon: Wind,
      kicker: stormy ? "Strong gales" : "Windy day",
      headline: `Gusts to ${Math.round(gusts)} km/h${dir ? ` from ${dir}` : ""}`,
      sub: stormy
        ? "Falling-debris risk. Secure loose items, avoid being under trees."
        : "Hold onto loose belongings. Comfortable temperature otherwise.",
      action: { Icon: Wind, label: stormy ? "Secure loose objects · stay clear of trees" : "Light layer that won't flap" },
    };
  }

  // -------- STRONG UV (clear day, daytime) --------
  if (isDay && uvPeak != null && uvPeak >= 8) {
    const xtreme = uvPeak >= 11;
    return {
      accent: xtreme ? "#dc2626" : "#ea580c", heroIcon: Sun,
      kicker: xtreme ? "Extreme UV" : "Strong UV",
      headline: `Peak UV ${Math.round(uvPeak)} today`,
      sub: xtreme
        ? "Sunburn in under 15 minutes. Hat, sunscreen, sunglasses essential."
        : "Sunburn risk midday. Apply SPF 30+ before going out.",
      action: { Icon: Sun, label: xtreme ? "Avoid 11 AM-3 PM sun" : "Sunscreen + hat outdoors" },
    };
  }

  // -------- RAIN COMING (dry now, wet 0-6 h) --------
  if (firstWetIdx !== -1 && startTime) {
    return {
      accent: "#0891b2", heroIcon: CloudSun,
      kicker: "Dry now",
      headline: `Rain expected ~${startTime}`,
      sub: peakMmh >= 5
        ? `Could be heavy — peak ${peakMmh.toFixed(1)} mm/h.`
        : `About ${expectedMm} mm expected through the day.`,
      action: { Icon: Umbrella, label: `Take an umbrella from about ${startTime}` },
      rainfall: { todayMm, expectedMm, peakMmh },
    };
  }

  // -------- RAIN COMING (nowcast peak ≥ 30 in 30 min) --------
  if (nowcastPeak >= 30 && nowcastPeakMin <= 30) {
    return {
      accent: "#1d4ed8", heroIcon: CloudRain,
      kicker: "Rain coming",
      headline: `Starts in ~${nowcastPeakMin} min`,
      sub: nowcastPeak >= 60
        ? "Heavy cell heading your way — take cover."
        : "A shower is moving in — head indoors.",
      action: { Icon: Umbrella, label: "Grab an umbrella now" },
    };
  }

  // -------- PLEASANT DEFAULT --------
  const cloudy = code != null && code >= 2 && code <= 3;
  const cool = t != null && t <= 22;
  return {
    accent: cloudy ? "#0891b2" : cool ? "#16a34a" : "#d97706",
    heroIcon: cloudy ? CloudSun : cool ? Leaf : Sun,
    kicker: cool ? "Pleasant & comfortable" : cloudy ? "Cloudy & calm" : "Clear & dry",
    headline: cool ? `${t.toFixed(0)}° · feels great outside` : "No rain in the next 6 hours",
    sub: cool
      ? "Light layer is ideal. Great day for a walk."
      : "Skies look settled — great time to be outside.",
    action: {
      Icon: cool ? Leaf : Sun,
      label: cool ? "Open windows · enjoy the breeze" : "No umbrella needed today",
    },
  };
}

/* ============================================================
 * MAIN
 * ============================================================ */

export default function WeatherDecisionCard({ forecast, nowcast, aqi, theme }) {
  const decision = useMemo(
    () => buildDecision({ forecast, nowcast, aqi }),
    [forecast, nowcast, aqi],
  );
  if (!forecast) return null;

  const { heroIcon: Hero, accent, kicker, headline, sub, action, rainfall } = decision;
  const Action = action.Icon;
  const showRainfall = rainfall && (rainfall.expectedMm >= 0.5 || rainfall.todayMm >= 0.5);
  const progressPct = rainfall && rainfall.expectedMm > 0
    ? Math.min(100, Math.round((rainfall.todayMm / rainfall.expectedMm) * 100))
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-[20px] overflow-hidden relative"
      style={{
        background: theme.surface,
        border: `1px solid ${accent}33`,
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        boxShadow: `0 1px 0 rgba(255,255,255,0.55) inset, 0 12px 28px -18px ${accent}55`,
      }}
      data-testid="rain-decision-card"
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(140% 100% at 0% 0%, ${accent}1c 0%, transparent 70%)`,
        }}
      />
      <div className="relative px-4 py-3.5">
        <div className="flex items-start gap-3">
          <div
            className="shrink-0 rounded-full flex items-center justify-center"
            style={{
              width: 52, height: 52,
              background: `${accent}1f`,
              border: `1.5px solid ${accent}55`,
            }}
          >
            <Hero size={28} weight="duotone" color={accent} />
          </div>
          <div className="flex-1 min-w-0">
            <div
              className="text-[9.5px] font-extrabold uppercase tracking-[0.18em]"
              style={{ color: accent }}
              data-testid="rain-decision-kicker"
            >
              {kicker}
            </div>
            <div
              className="leading-tight mt-0.5"
              style={{ ...NUM, fontSize: 18, color: theme.textPrimary }}
              data-testid="rain-decision-headline"
            >
              {headline}
            </div>
            <p
              className="text-[11.5px] mt-1 leading-snug font-medium"
              style={{ color: theme.textSecondary }}
              data-testid="rain-decision-sub"
            >
              {sub}
            </p>
          </div>
        </div>

        {showRainfall && (
          <div
            className="mt-3 pt-2.5"
            style={{ borderTop: `1px solid ${theme.surfaceBorder}` }}
            data-testid="rain-decision-rainfall"
          >
            <div className="flex items-baseline gap-1.5">
              <Drop size={11} weight="duotone" color={accent} />
              <span
                className="text-[9.5px] font-extrabold uppercase tracking-[0.16em]"
                style={{ color: theme.textMuted }}
              >
                Today's rainfall
              </span>
              <span
                className="ml-auto tabular-nums"
                style={{ ...NUM, fontSize: 14, color: theme.textPrimary }}
              >
                {rainfall.todayMm.toFixed(1)}<span className="text-[10px] font-bold ml-0.5" style={{ color: theme.textMuted }}>mm</span>
                <span className="text-[11px] mx-1" style={{ color: theme.textMuted }}>/</span>
                {rainfall.expectedMm.toFixed(0)}<span className="text-[10px] font-bold ml-0.5" style={{ color: theme.textMuted }}>mm expected</span>
              </span>
            </div>
            <div className="mt-1.5 h-1.5 rounded-full overflow-hidden" style={{ background: `${accent}1f` }}>
              <motion.div
                className="h-full rounded-full"
                style={{ background: accent }}
                initial={{ width: 0 }}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 0.9, ease: "easeOut" }}
              />
            </div>
            {rainfall.peakMmh > 0 && (
              <div className="text-[10px] mt-1 font-medium" style={{ color: theme.textMuted }}>
                Peak intensity {rainfall.peakMmh.toFixed(1)} mm/h
                {rainfall.expectedMm >= 30 ? " · expect waterlogging in low areas" : ""}
              </div>
            )}
          </div>
        )}

        <div
          className="mt-3 -mx-4 -mb-3.5 px-4 py-2.5 flex items-center gap-2"
          style={{ background: `${accent}14`, borderTop: `1px solid ${accent}26` }}
          data-testid="rain-decision-action"
        >
          <Action size={16} weight="duotone" color={accent} />
          <span
            className="text-[13px] font-extrabold leading-tight"
            style={{ color: theme.textPrimary, letterSpacing: "-0.01em" }}
          >
            {action.label}
          </span>
        </div>
      </div>
    </motion.div>
  );
}
